import React, { useEffect, useMemo, useState } from 'react';
import { AdminLayout } from '../components/layout/AdminLayout';
import { consultationChargesAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  IndianRupee, Save, Loader2, Search, Building2, ChevronDown, ChevronRight,
  AlertCircle, CheckCircle2, RotateCcw, Lock, KeyRound, ShieldCheck,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

export default function ConsultationCharges() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState({});
  const [edits, setEdits] = useState({}); // { "collegeId::courseId": amount }
  const [totalConfigured, setTotalConfigured] = useState(0);

  // Password gate
  const [unlocked, setUnlocked] = useState(() => sessionStorage.getItem('consultation_unlocked') === '1');
  const [gatePwd, setGatePwd] = useState('');
  const [gateError, setGateError] = useState('');
  const [unlocking, setUnlocking] = useState(false);
  const [hasCustomPwd, setHasCustomPwd] = useState(true);
  const [pwdDialogOpen, setPwdDialogOpen] = useState(false);
  const [pwdForm, setPwdForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [pwdSaving, setPwdSaving] = useState(false);

  useEffect(() => {
    consultationChargesAPI.gateStatus()
      .then((r) => setHasCustomPwd(!!r.data.has_custom_password))
      .catch(() => {});
  }, []);

  const doUnlock = async (e) => {
    e && e.preventDefault();
    setGateError('');
    if (!gatePwd) { setGateError('Please enter the password'); return; }
    setUnlocking(true);
    try {
      await consultationChargesAPI.gateUnlock(gatePwd);
      sessionStorage.setItem('consultation_unlocked', '1');
      setUnlocked(true);
      setGatePwd('');
    } catch (err) {
      setGateError(err.response?.data?.detail || 'Incorrect password');
    } finally {
      setUnlocking(false);
    }
  };

  const doLock = () => {
    sessionStorage.removeItem('consultation_unlocked');
    setUnlocked(false);
  };

  const doChangePassword = async () => {
    if (pwdForm.new_password.length < 4) { toast.error('New password must be at least 4 characters'); return; }
    if (pwdForm.new_password !== pwdForm.confirm) { toast.error('Passwords do not match'); return; }
    setPwdSaving(true);
    try {
      await consultationChargesAPI.gateSetPassword({
        current_password: hasCustomPwd ? pwdForm.current_password : undefined,
        new_password: pwdForm.new_password,
      });
      toast.success('Consultation Charges password updated');
      setPwdDialogOpen(false);
      setPwdForm({ current_password: '', new_password: '', confirm: '' });
      setHasCustomPwd(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    } finally {
      setPwdSaving(false);
    }
  };

  const fetchMatrix = async () => {
    setLoading(true);
    try {
      const { data } = await consultationChargesAPI.matrix();
      setRows(data.rows || []);
      setTotalConfigured(data.total_configured || 0);
      setEdits({});
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (unlocked) fetchMatrix(); }, [unlocked]);

  const filtered = useMemo(() => {
    if (!query.trim()) return rows;
    const low = query.toLowerCase();
    return rows
      .map((r) => {
        const match = r.college_name.toLowerCase().includes(low) || r.city.toLowerCase().includes(low);
        const courseMatches = r.courses.filter((c) => c.course_name.toLowerCase().includes(low));
        if (match) return r;
        if (courseMatches.length) return { ...r, courses: courseMatches };
        return null;
      })
      .filter(Boolean);
  }, [rows, query]);

  const updateAmount = (collegeId, courseId, amount) => {
    const key = `${collegeId}::${courseId}`;
    setEdits((e) => ({ ...e, [key]: amount }));
  };

  const revertAmount = (collegeId, courseId) => {
    const key = `${collegeId}::${courseId}`;
    const next = { ...edits };
    delete next[key];
    setEdits(next);
  };

  const dirtyCount = Object.keys(edits).length;

  const saveAll = async () => {
    if (!dirtyCount) return;
    setSaving(true);
    try {
      const items = [];
      for (const r of rows) {
        for (const c of r.courses) {
          const key = `${r.college_id}::${c.course_id}`;
          if (edits[key] !== undefined) {
            items.push({
              college_id: r.college_id,
              college_name: r.college_name,
              course_id: c.course_id,
              course_name: c.course_name,
              amount: Number(edits[key]) || 0,
            });
          }
        }
      }
      const { data } = await consultationChargesAPI.bulkUpsert(items);
      toast.success(`Saved ${data.updated} row${data.updated === 1 ? '' : 's'}`);
      await fetchMatrix();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const toggleCollege = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  const expandAll = () => {
    const next = {};
    filtered.forEach((r) => { next[r.college_id] = true; });
    setExpanded(next);
  };

  const collapseAll = () => setExpanded({});

  const summary = useMemo(() => {
    let totalCourses = 0, configured = 0, totalAmount = 0;
    for (const r of rows) {
      for (const c of r.courses) {
        totalCourses += 1;
        const key = `${r.college_id}::${c.course_id}`;
        const amt = edits[key] !== undefined ? Number(edits[key]) : Number(c.amount || 0);
        if (amt > 0) configured += 1;
        totalAmount += amt;
      }
    }
    const avgPerAdmission = configured > 0 ? totalAmount / configured : 0;
    return { totalCourses, configured, totalAmount, avgPerAdmission };
  }, [rows, edits]);

  return (
    <AdminLayout>
      {!unlocked ? (
        <div className="max-w-md mx-auto px-4 py-16">
          <Card className="border-0 shadow-xl">
            <CardContent className="p-8">
              <div className="flex flex-col items-center text-center mb-6">
                <div className="h-14 w-14 rounded-full bg-gradient-to-br from-[#0066CC] to-[#003d7a] text-white flex items-center justify-center mb-3 shadow-lg">
                  <Lock className="h-7 w-7" />
                </div>
                <h1 className="text-xl font-semibold text-slate-900">Consultation Charges Locked</h1>
                <p className="text-sm text-slate-500 mt-1">
                  Pricing data is sensitive. Enter the secondary password to continue.
                </p>
                {!hasCustomPwd && (
                  <p className="text-[11px] text-amber-600 mt-3 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                    Default password is in use. Change it from the dialog below after unlocking.
                  </p>
                )}
              </div>
              <form onSubmit={doUnlock} className="space-y-3" data-testid="cc-gate-form">
                <div>
                  <Label>Password</Label>
                  <Input
                    type="password"
                    value={gatePwd}
                    onChange={(e) => setGatePwd(e.target.value)}
                    autoFocus
                    placeholder="Enter Consultation Charges password"
                    data-testid="cc-gate-password"
                  />
                  {gateError && <p className="text-xs text-rose-600 mt-1" data-testid="cc-gate-error">{gateError}</p>}
                </div>
                <Button
                  type="submit"
                  disabled={unlocking}
                  className="w-full bg-[#0066CC] hover:bg-[#0052A3]"
                  data-testid="cc-gate-submit"
                >
                  {unlocking ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <KeyRound className="h-4 w-4 mr-1" />}
                  Unlock
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      ) : (
      <div className="space-y-6 p-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
              <IndianRupee className="h-6 w-6 text-[#0066CC]" />
              Consultation Charges
              <Badge variant="outline" className="bg-emerald-50 border-emerald-200 text-emerald-700 gap-1 ml-2">
                <ShieldCheck className="h-3 w-3" /> Unlocked
              </Badge>
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Set consultation charges (revenue) per admission for each course of every featured college.
              Only Admin can update.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setPwdDialogOpen(true)} data-testid="cc-change-pwd-btn">
              <KeyRound className="h-4 w-4 mr-1" /> {hasCustomPwd ? 'Change Password' : 'Set Password'}
            </Button>
            <Button variant="outline" size="sm" onClick={doLock} data-testid="cc-lock-btn">
              <Lock className="h-4 w-4 mr-1" /> Lock
            </Button>
            {dirtyCount > 0 && (
              <Badge className="bg-amber-100 text-amber-800">
                <AlertCircle className="h-3 w-3 mr-1" /> {dirtyCount} unsaved
              </Badge>
            )}
            <Button
              onClick={saveAll}
              disabled={!dirtyCount || saving}
              className="bg-[#0066CC] hover:bg-[#0052A3]"
              data-testid="save-consultation-charges"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
              Save Changes
            </Button>
          </div>
        </div>

        {/* Summary tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <p className="text-[11px] uppercase tracking-wider text-slate-500">Featured Colleges</p>
              <p className="text-2xl font-semibold text-slate-900 mt-1">{rows.length}</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <p className="text-[11px] uppercase tracking-wider text-slate-500">Total Courses</p>
              <p className="text-2xl font-semibold text-slate-900 mt-1">{summary.totalCourses}</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm border-l-4 border-l-emerald-500">
            <CardContent className="p-4">
              <p className="text-[11px] uppercase tracking-wider text-slate-500">Configured</p>
              <p className="text-2xl font-semibold text-emerald-600 mt-1">{summary.configured}</p>
              <p className="text-[10px] text-slate-400 mt-0.5">
                {summary.totalCourses > 0 ? Math.round((summary.configured / summary.totalCourses) * 100) : 0}% of courses
              </p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <p className="text-[11px] uppercase tracking-wider text-slate-500">Total Potential per Admission</p>
              <p className="text-2xl font-semibold text-[#0066CC] mt-1">{inr(Math.round(summary.avgPerAdmission))}</p>
              <p className="text-[10px] text-slate-400 mt-0.5">
                Avg of {summary.configured} configured · Sum {inr(summary.totalAmount)}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Controls */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search college or course..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="pl-9"
                data-testid="cc-search"
              />
            </div>
            <Button variant="outline" size="sm" onClick={expandAll}>Expand all</Button>
            <Button variant="outline" size="sm" onClick={collapseAll}>Collapse all</Button>
            <Button variant="ghost" size="sm" onClick={fetchMatrix} disabled={loading}>
              <RotateCcw className="h-4 w-4 mr-1" /> Refresh
            </Button>
          </CardContent>
        </Card>

        {/* Matrix */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center p-10 text-slate-500">
                <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <Building2 className="h-10 w-10 mx-auto mb-2 text-slate-300" />
                <p>No colleges match your search.</p>
              </div>
            ) : (
              <div className="divide-y">
                {filtered.map((r) => {
                  const isOpen = !!expanded[r.college_id] || !!query.trim();
                  const configuredCount = r.courses.filter((c) => {
                    const key = `${r.college_id}::${c.course_id}`;
                    const amt = edits[key] !== undefined ? Number(edits[key]) : Number(c.amount || 0);
                    return amt > 0;
                  }).length;
                  const pendingCount = r.courses.filter((c) => edits[`${r.college_id}::${c.course_id}`] !== undefined).length;

                  return (
                    <div key={r.college_id} data-testid={`college-block-${r.college_id}`}>
                      <button
                        type="button"
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 text-left"
                        onClick={() => toggleCollege(r.college_id)}
                      >
                        {isOpen ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
                        <Building2 className="h-4 w-4 text-[#0066CC]" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">{r.college_name}</p>
                          {r.city && <p className="text-xs text-slate-500">{r.city}</p>}
                        </div>
                        <div className="flex items-center gap-2 text-xs">
                          {pendingCount > 0 && (
                            <Badge className="bg-amber-100 text-amber-800">
                              {pendingCount} edit{pendingCount === 1 ? '' : 's'}
                            </Badge>
                          )}
                          <Badge variant="outline" className={configuredCount === r.courses.length ? 'border-emerald-300 text-emerald-700 bg-emerald-50' : ''}>
                            {configuredCount}/{r.courses.length} configured
                          </Badge>
                        </div>
                      </button>
                      {isOpen && (
                        <div className="bg-slate-50/70 border-t">
                          <Table>
                            <TableHeader>
                              <TableRow className="hover:bg-transparent">
                                <TableHead>Course</TableHead>
                                <TableHead className="w-56">Consultation Charge (₹)</TableHead>
                                <TableHead className="w-24 text-right">Status</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {r.courses.map((c) => {
                                const key = `${r.college_id}::${c.course_id}`;
                                const edited = edits[key] !== undefined;
                                const value = edited ? edits[key] : (c.amount || '');
                                const effective = edited ? Number(edits[key] || 0) : Number(c.amount || 0);
                                return (
                                  <TableRow key={c.course_id} className="hover:bg-white">
                                    <TableCell className="py-2">
                                      <div className="text-sm text-slate-800">{c.course_name}</div>
                                      <div className="text-[11px] text-slate-400">{c.course_id}</div>
                                    </TableCell>
                                    <TableCell className="py-2">
                                      <div className="flex items-center gap-1">
                                        <span className="text-slate-400 text-sm">₹</span>
                                        <Input
                                          type="number"
                                          min="0"
                                          step="100"
                                          value={value}
                                          onChange={(e) => updateAmount(r.college_id, c.course_id, e.target.value)}
                                          className={`h-8 w-36 ${edited ? 'border-amber-400 bg-amber-50' : ''}`}
                                          placeholder="0"
                                          data-testid={`amount-${c.course_id}`}
                                        />
                                        {edited && (
                                          <Button variant="ghost" size="sm" onClick={() => revertAmount(r.college_id, c.course_id)} className="h-8 px-2 text-xs">
                                            Revert
                                          </Button>
                                        )}
                                      </div>
                                    </TableCell>
                                    <TableCell className="text-right py-2">
                                      {effective > 0 ? (
                                        <Badge className="bg-emerald-100 text-emerald-700">
                                          <CheckCircle2 className="h-3 w-3 mr-1" /> Set
                                        </Badge>
                                      ) : (
                                        <span className="text-xs text-slate-400">Not set</span>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {dirtyCount > 0 && (
          <div className="sticky bottom-4 flex justify-end">
            <Card className="border-0 shadow-xl bg-[#0066CC] text-white">
              <CardContent className="flex items-center gap-3 p-3">
                <AlertCircle className="h-5 w-5" />
                <span className="text-sm">{dirtyCount} unsaved change{dirtyCount === 1 ? '' : 's'}</span>
                <Button
                  onClick={saveAll}
                  disabled={saving}
                  className="bg-white text-[#0066CC] hover:bg-slate-100 h-8"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
                  Save
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
      )}

      {/* Change Password Dialog */}
      <Dialog open={pwdDialogOpen} onOpenChange={setPwdDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5" /> {hasCustomPwd ? 'Change' : 'Set'} Consultation Charges Password</DialogTitle>
            <DialogDescription>
              {hasCustomPwd
                ? 'Updates the password required to open this page. Existing sessions stay unlocked until refreshed.'
                : 'You are using the default password. Set your own to restrict access.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {hasCustomPwd && (
              <div>
                <Label>Current Password</Label>
                <Input type="password" value={pwdForm.current_password} onChange={(e) => setPwdForm({ ...pwdForm, current_password: e.target.value })} data-testid="cc-cur-pwd" />
              </div>
            )}
            <div>
              <Label>New Password</Label>
              <Input type="password" value={pwdForm.new_password} onChange={(e) => setPwdForm({ ...pwdForm, new_password: e.target.value })} data-testid="cc-new-pwd" />
            </div>
            <div>
              <Label>Confirm New Password</Label>
              <Input type="password" value={pwdForm.confirm} onChange={(e) => setPwdForm({ ...pwdForm, confirm: e.target.value })} data-testid="cc-confirm-pwd" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwdDialogOpen(false)}>Cancel</Button>
            <Button onClick={doChangePassword} disabled={pwdSaving} className="bg-[#0066CC] hover:bg-[#0052A3]" data-testid="cc-save-pwd">
              {pwdSaving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
              Save Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
